import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dining-guide-3/artifacts/egfzykn3_image.png";

const Navigation = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#home', label: t('nav.home') },
    { href: '#speisekarte', label: t('nav.menu') },
    { href: '#mittagessen', label: t('nav.lunch') },
    { href: '#catering', label: 'B2B', isSpecial: true },
    { href: '#reservation', label: language === 'de' ? 'Reservierung' : 'Reservation' },
    { href: '#about', label: t('nav.about') },
    { href: '#contact', label: t('nav.contact') },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'py-2 glass-dark shadow-lg'
            : 'py-4 bg-hanse-navy/95'
        }`}
        data-testid="main-navigation"
      >
        <div className="container mx-auto px-4 flex items-center justify-between">
          {/* Logo */}
          <a href="#home" onClick={(e) => scrollToSection(e, '#home')} className="flex items-center gap-3">
            <motion.img
              src={LOGO_URL}
              alt="Hanse Pizza"
              className={`rounded-full border-2 border-hanse-gold/50 transition-all duration-300 ${
                isScrolled ? 'w-10 h-10' : 'w-12 h-12'
              }`}
              whileHover={{ rotate: 10 }}
              data-testid="nav-logo"
            />
            <span className={`font-heading font-bold transition-all duration-300 ${
              isScrolled ? 'text-lg' : 'text-xl'
            }`}>
              <span className="text-hanse-red">HANSE</span>{' '}
              <span className="text-white">PIZZA</span>
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className={`transition-colors font-medium text-sm tracking-wide ${
                  link.isSpecial 
                    ? 'text-amber-400 hover:text-amber-300 px-3 py-1.5 bg-amber-400/10 rounded-full border border-amber-400/30 hover:border-amber-400/60' 
                    : 'text-white/90 hover:text-hanse-gold'
                }`}
                data-testid={`nav-link-${link.href.slice(1)}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Right Section */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 text-white/90 hover:text-hanse-gold transition-colors px-3 py-2 rounded-full border border-white/20 hover:border-hanse-gold/50"
              data-testid="language-toggle"
            >
              <Globe className="w-4 h-4" />
              <span className="font-semibold text-sm">{language.toUpperCase()}</span>
            </button>

            {/* Phone */}
            <a
              href="tel:+4942148535145"
              className="flex items-center gap-2 bg-hanse-red hover:bg-hanse-red/90 text-white px-4 py-2 rounded-full font-semibold text-sm transition-all hover:scale-105"
              data-testid="nav-call-btn"
            >
              <Phone className="w-4 h-4" />
              {t('nav.callNow')}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex lg:hidden items-center gap-3 z-50">
            <button
              onClick={toggleLanguage}
              className="text-white/90 hover:text-hanse-gold p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
              data-testid="mobile-language-toggle"
              aria-label="Toggle language"
            >
              <Globe className="w-5 h-5" />
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-white p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed inset-0 z-40 bg-hanse-navy pt-20"
            data-testid="mobile-menu"
          >
            <div className="container mx-auto px-4 py-8">
              <div className="flex flex-col gap-4">
                {navLinks.map((link, index) => (
                  <motion.a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`text-xl font-heading py-3 border-b border-white/10 transition-colors ${
                      link.isSpecial 
                        ? 'text-amber-400 hover:text-amber-300 flex items-center gap-2' 
                        : 'text-white hover:text-hanse-gold'
                    }`}
                    data-testid={`mobile-nav-link-${link.href.slice(1)}`}
                  >
                    {link.isSpecial && <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"></span>}
                    {link.label}
                  </motion.a>
                ))}

                <motion.a
                  href="tel:+4942148535145"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="mt-6 flex items-center justify-center gap-2 bg-hanse-red text-white px-6 py-4 rounded-full font-bold text-lg"
                  data-testid="mobile-call-btn"
                >
                  <Phone className="w-5 h-5" />
                  +49 421 48535145
                </motion.a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
